import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Eye, Leaf, Sprout, ArrowDown, Compass } from "lucide-react";
import { SITE } from "../config/site";
import { scrollToId } from "../lib/scroll";
import { track } from "../lib/analytics";
import { useContent } from "../context/ContentContext";

const LINES = [
  { text: "You're carrying a lot.", accent: null },
  { text: "Let's make room", accent: null },
  { text: "for your ", accent: "health." },
];

const EASE = [0.22, 1, 0.36, 1];

const Badge = ({ icon: Icon, label, className, slow }) => (
  <div
    className={`${slow ? "drift-slow" : "drift"} absolute flex items-center gap-2 rounded-full border border-forest/10 bg-ivory/90 px-4 py-2 shadow-[0_12px_30px_rgba(23,63,53,0.12)] backdrop-blur-md ${className}`}
  >
    <Icon className="h-4 w-4 text-gold" />
    <span className="text-xs font-semibold tracking-wide text-forest">{label}</span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const heroIntro = useContent(
    "hero_intro",
    "Holistic wellness, conscious living, and spiritual education for women and mothers. A calm, unhurried space to understand your body, quiet your mind, and grow into meaningful change."
  );
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-[76px]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-sage/60 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 -left-52 h-[480px] w-[480px] rounded-full bg-gold/10 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-20">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-7 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.35em] text-gold"
          >
            <span className="h-px w-10 bg-gold" aria-hidden="true" />
            Rooted Healing by Staci Freed
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl leading-[1.12] tracking-tight text-forest sm:text-5xl lg:text-[4.4rem]"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                  {line.accent && <em className="italic text-gold">{line.accent}</em>}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-lg font-light leading-relaxed text-charcoal/75"
          >
            {heroIntro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-explore-consultations-button"
              onClick={() => scrollToId("#consultations")}
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_14px_35px_rgba(23,63,53,0.28)] hover:-translate-y-0.5"
            >
              Explore Consultations
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link
              to="/wellness-discovery"
              data-testid="hero-discover-wellness-button"
              onClick={() => track("discovery_cta_click", { location: "hero" })}
              className="group inline-flex items-center gap-2 rounded-full border border-forest/30 px-7 py-4 text-sm font-semibold text-forest transition-all duration-300 hover:border-gold hover:text-gold-dark"
            >
              <Compass className="h-4 w-4 text-gold" />
              Discover Your Wellness Profile
            </Link>
            <button
              data-testid="hero-join-newsletter-link"
              onClick={() => scrollToId("#newsletter")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-forest transition-colors duration-300 hover:text-gold-dark"
            >
              <span className="border-b border-forest/30 pb-0.5 transition-colors duration-300 group-hover:border-gold">
                Join My Newsletter
              </span>
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
            className="relative mx-auto max-w-md"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-3 translate-x-5 translate-y-5 rounded-t-full rounded-b-[2.5rem] border border-gold/40"
            />
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden rounded-t-full rounded-b-[2.5rem] shadow-[0_30px_60px_rgba(23,63,53,0.25)]"
            >
              <img
                src={SITE.images.hero}
                alt="Staci Freed seated in quiet meditation beside a flowing river"
                className="aspect-[4/5.4] w-full object-cover"
                loading="eager"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-forest/5" />
            </motion.div>
            <Badge icon={Leaf} label="Herbal Wisdom" className="-left-6 top-16 hidden sm:flex" />
            <Badge icon={Eye} label="Iridology" className="-right-4 top-1/3 hidden sm:flex" slow />
            <Badge icon={Sprout} label="Spiritual Education" className="-left-8 bottom-24 hidden sm:flex" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
